import { useState } from "react";
import { Trash2 } from "lucide-react";
import Modal from "./Modal";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtMoney, projectNumber } from "../lib/format";
import { projectRawTotal } from "../lib/calc";

// Admin-only trash-can for project rows. Confirmation dialog names the
// project, number, and raw total. Deletion snapshots the whole dataset
// on the server so it's one-click recoverable from Backups.
export default function DeleteProjectButton({ project, isAdmin, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!isAdmin || !project) return null;

  const raw = projectRawTotal(project);
  const scopeCount = (project.scopes || []).filter((s) => !s.isSubConsultant).length;
  const number = projectNumber(project);

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/data/projects/${encodeURIComponent(project.id)}`);
      toast.success(`Deleted ${project.name}. Recover from Backups if needed.`);
      setOpen(false);
      if (onDeleted) await onDeleted(project);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the project");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className="inline-flex items-center justify-center h-11 w-11 text-[var(--soft-fg)] opacity-70 hover:opacity-100 hover:text-[#6B2F2A] hover:bg-[#FBF3F2] focus-visible:opacity-100 focus-visible:text-[#6B2F2A] focus-visible:bg-[#FBF3F2] transition-colors"
        title="Delete project (admin only)"
        aria-label={`Delete project ${project.name}`}
        data-testid={`delete-project-${project.id}`}
      >
        <Trash2 size={15} />
      </button>
      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        width="max-w-lg"
        testid="delete-project-dialog"
      >
        <div className="eyebrow text-[#6B2F2A] mb-2">Delete project</div>
        <div className="font-display text-lg text-[var(--ink)] mb-3">
          Delete {project.name} ({number}) — {fmtMoney(raw)} across {scopeCount} scope{scopeCount === 1 ? "" : "s"}?
        </div>
        <div className="text-sm text-[var(--muted-fg)] mb-6">
          This removes its forecasts, allocations, and invoicing.
          A snapshot is saved first — one click from Backups restores it exactly.
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={busy}
            className="px-4 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
            data-testid="delete-project-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#6B2F2A] text-white text-xs font-display tracked-tight uppercase hover:opacity-90 disabled:opacity-40"
            data-testid="delete-project-confirm"
          >
            <Trash2 size={12} /> {busy ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </Modal>
    </>
  );
}
